import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams, useLocation } from 'react-router-dom';
import { AppProvider, useApp } from './context/AppContext';
import { ThemeProvider } from './context/ThemeContext';
import PrivateRoute from './components/shared/PrivateRoute';
import AppLayout from './components/shared/AppLayout';
import EntryPage from './pages/EntryPage';
import LoginPage from './pages/LoginPage';
import CallbackPage from './pages/CallbackPage';
import HomePage from './pages/HomePage';
import TicketListPage from './pages/user/TicketListPage';
import UserTicketDetailPage from './pages/user/UserTicketDetailPage';
import TicketsListPage from './pages/allTickets/TicketsListPage';
import AllTicketsDetailPage from './pages/allTickets/AllTicketsDetailPage';
import ResolvedClosedByResponsiblePage from './pages/dashboards/ResolvedClosedByResponsiblePage';
import TotalTicketsByAppPage from './pages/dashboards/TotalTicketsByAppPage';
import TotalByCreatedUserPage from './pages/dashboards/TotalByCreatedUserPage';
import TicketStatusesAdminPage from './pages/admin/TicketStatusesAdminPage';
import DemandTypesAdminPage from './pages/admin/DemandTypesAdminPage';
import AppOriginsAdminPage from './pages/admin/AppOriginsAdminPage';
import ResolutionTypesAdminPage from './pages/admin/ResolutionTypesAdminPage';
import UsersAdminPage from './pages/admin/UsersAdminPage';
import SettingsHubPage from './pages/admin/SettingsHubPage';
import ResponsiblePerformanceOverTimePage from './pages/dashboards/ResponsiblePerformanceOverTimePage';
import SupportResolutionBreakdownPage from './pages/dashboards/SupportResolutionBreakdownPage';
import TvDashboardPage from './pages/dashboards/TvDashboardPage';
import WidgetsAdminPage from './pages/admin/WidgetsAdminPage';
import EmbedUserTicketsPage from './pages/embed/EmbedUserTicketsPage';
import EmbedUserTicketDetailPage from './pages/embed/EmbedUserTicketDetailPage';
import EmbedLayout from './pages/embed/EmbedLayout';
import Toast from './components/shared/Toast';

/** Compatibilidade: URLs antigas /support/tickets → /all-tickets */
const RedirectLegacyAllTicketsList = () => {
  const location = useLocation();
  return <Navigate to={`/all-tickets${location.search}`} replace />;
};

const RedirectLegacyAllTicketsDetail = () => {
  const { id } = useParams();
  return <Navigate to={`/all-tickets/${id}`} replace />;
};

const AppContent = () => {
  const app = useApp();
  const toasts = app?.toasts ?? [];
  const removeToast = app?.removeToast ?? (() => {});

  return (
    <div className="app-container">
      <Routes>
        <Route path="/" element={<EntryPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/callback" element={<CallbackPage />} />
        <Route
          path="/home"
          element={
            <PrivateRoute>
              <AppLayout>
                <HomePage />
              </AppLayout>
            </PrivateRoute>
          }
        />
        <Route 
          path="/user/tickets" 
          element={
            <PrivateRoute>
              <AppLayout>
                <TicketListPage />
              </AppLayout>
            </PrivateRoute>
          } 
        />
        <Route 
          path="/user/tickets/:id" 
          element={
            <PrivateRoute>
              <AppLayout>
                <UserTicketDetailPage />
              </AppLayout>
            </PrivateRoute>
          } 
        />
        <Route
          path="/all-tickets"
          element={
            <PrivateRoute>
              <AppLayout>
                <TicketsListPage />
              </AppLayout>
            </PrivateRoute>
          }
        />
        <Route
          path="/tickets/support"
          element={
            <PrivateRoute>
              <AppLayout>
                <TicketsListPage />
              </AppLayout>
            </PrivateRoute>
          }
        />
        <Route
          path="/tickets/improvement"
          element={
            <PrivateRoute>
              <AppLayout>
                <TicketsListPage />
              </AppLayout>
            </PrivateRoute>
          }
        />
        <Route
          path="/tickets/project"
          element={
            <PrivateRoute>
              <AppLayout>
                <TicketsListPage />
              </AppLayout>
            </PrivateRoute>
          }
        />
        <Route
          path="/tickets/other"
          element={
            <PrivateRoute>
              <AppLayout>
                <TicketsListPage />
              </AppLayout>
            </PrivateRoute>
          }
        />
        <Route
          path="/all-tickets/:id"
          element={
            <PrivateRoute>
              <AppLayout>
                <AllTicketsDetailPage />
              </AppLayout>
            </PrivateRoute>
          }
        />
        <Route
          path="/support/tickets"
          element={
            <PrivateRoute>
              <RedirectLegacyAllTicketsList />
            </PrivateRoute>
          }
        />
        <Route
          path="/support/tickets/:id"
          element={
            <PrivateRoute>
              <RedirectLegacyAllTicketsDetail />
            </PrivateRoute>
          }
        />
        <Route
          path="/dashboards/tv"
          element={
            <PrivateRoute>
              <TvDashboardPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/dashboards"
          element={
            <PrivateRoute>
              <AppLayout>
                <Navigate to="/dashboards/total-by-app" replace />
              </AppLayout>
            </PrivateRoute>
          }
        />
        <Route
          path="/dashboards/resolved-by-responsible"
          element={
            <PrivateRoute>
              <AppLayout>
                <ResolvedClosedByResponsiblePage />
              </AppLayout>
            </PrivateRoute>
          }
        />
        <Route
          path="/dashboards/responsible-performance-over-time"
          element={
            <PrivateRoute>
              <AppLayout>
                <ResponsiblePerformanceOverTimePage />
              </AppLayout>
            </PrivateRoute>
          }
        />
        <Route
          path="/dashboards/support-resolution-breakdown"
          element={
            <PrivateRoute>
              <AppLayout>
                <SupportResolutionBreakdownPage />
              </AppLayout>
            </PrivateRoute>
          }
        />
        <Route
          path="/dashboards/total-by-app"
          element={
            <PrivateRoute>
              <AppLayout>
                <TotalTicketsByAppPage />
              </AppLayout>
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/settings"
          element={
            <PrivateRoute>
              <AppLayout>
                <SettingsHubPage />
              </AppLayout>
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/ticket-statuses"
          element={
            <PrivateRoute>
              <AppLayout>
                <TicketStatusesAdminPage />
              </AppLayout>
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/demand-types"
          element={
            <PrivateRoute>
              <AppLayout>
                <DemandTypesAdminPage />
              </AppLayout>
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/app-origins"
          element={
            <PrivateRoute>
              <AppLayout>
                <AppOriginsAdminPage />
              </AppLayout>
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/resolution-types"
          element={
            <PrivateRoute>
              <AppLayout>
                <ResolutionTypesAdminPage />
              </AppLayout>
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/users"
          element={
            <PrivateRoute>
              <AppLayout>
                <UsersAdminPage />
              </AppLayout>
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/widgets"
          element={
            <PrivateRoute>
              <AppLayout>
                <WidgetsAdminPage />
              </AppLayout>
            </PrivateRoute>
          }
        />
        <Route path="/embed" element={<EmbedLayout />}>
          <Route path="user/tickets" element={<EmbedUserTicketsPage />} />
          <Route path="user/tickets/:id" element={<EmbedUserTicketDetailPage />} />
        </Route>
        <Route
          path="/dashboards/total-by-created-user"
          element={
            <PrivateRoute>
              <AppLayout>
                <TotalByCreatedUserPage />
              </AppLayout>
            </PrivateRoute>
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <Toast toasts={toasts} removeToast={removeToast} />
    </div>
  );
};

const App = () => {
  return (
    <Router>
      <ThemeProvider>
        <AppProvider>
          <AppContent />
        </AppProvider>
      </ThemeProvider>
    </Router>
  );
};

export default App;
